import React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AC_FILES, ACC_FILES, AC_EVO_FILES } from "../results/files.config";
import { aceRaceRows } from "../utils/aceResults";
import { DROP_ROUNDS } from "../utils/points";

const POINTS_TABLE = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];
const COLORS = [
  "#1fccdc",
  "#f7383b",
  "#ffc400",
  "#00e676",
  "#ff7a00",
  "#b388ff",
  "#64ffda",
  "#ff80ab",
  "#90caf9",
  "#c6ff00",
];
const MOBILE_CHART_MIN_WIDTH = 620;

const __historyJsonPromiseCache = new Map();

function normalizeId(id) {
  if (!id) return "";
  const s = String(id);
  return s.startsWith("S") ? s.slice(1) : s;
}

function getPointsForPosition(positionIndex) {
  return POINTS_TABLE[positionIndex] ?? 0;
}

function stripBom(s) {
  return s && s.charCodeAt(0) === 0xfeff ? s.slice(1) : s;
}

function isGzip(u8) {
  return u8 && u8.length > 2 && u8[0] === 0x1f && u8[1] === 0x8b;
}

async function gunzipIfSupported(buf) {
  if (typeof DecompressionStream === "function") {
    const ds = new DecompressionStream("gzip");
    const stream = new Response(buf).body.pipeThrough(ds);
    return await new Response(stream).arrayBuffer();
  }
  return buf;
}

function tryParseWithDecoders(buf) {
  const decoders = ["utf-8", "utf-16le", "utf-16be"];
  for (const enc of decoders) {
    try {
      const txt = new TextDecoder(enc, { fatal: false }).decode(buf);
      return JSON.parse(stripBom(txt));
    } catch {
      // Try the next common export encoding.
    }
  }
  throw new Error("Unrecognized encoding");
}

async function fetchJson(url) {
  const key = String(url);
  if (__historyJsonPromiseCache.has(key)) return __historyJsonPromiseCache.get(key);

  const promise = (async () => {
    try {
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      let buf = await res.arrayBuffer();
      let u8 = new Uint8Array(buf);
      if (isGzip(u8)) {
        buf = await gunzipIfSupported(buf);
        u8 = new Uint8Array(buf);
      }
      return tryParseWithDecoders(buf);
    } catch (e) {
      return { __error: e.message || String(e) };
    }
  })();

  __historyJsonPromiseCache.set(key, promise);
  promise.catch(() => __historyJsonPromiseCache.delete(key));
  return promise;
}

function parseDateFromFilename(name) {
  const base = String(name || "").split("/").pop().replace(".json", "");
  const acMatch = base.match(/^(\d{4})_(\d{1,2})_(\d{1,2})_(\d{1,2})_(\d{1,2})_/);
  if (acMatch) {
    const [, y, m, d, h, min] = acMatch;
    const dt = new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(min));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const accMatch = base.match(/^(\d{2})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_/);
  if (accMatch) {
    const [, y, m, d, h, min, sec] = accMatch;
    const fullYear = Number(y) + (Number(y) < 80 ? 2000 : 1900);
    const dt = new Date(fullYear, Number(m) - 1, Number(d), Number(h), Number(min), Number(sec));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const aceMatch = base.match(/^results_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_/i);
  if (aceMatch) {
    const [, y, m, d, h, min, sec] = aceMatch;
    const dt = new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(min), Number(sec));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  return null;
}

function titleCaseTrack(track) {
  return String(track || "Track")
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function roundFromACC(json, fallback) {
  const serverName = json?.serverName || "";
  const match = serverName.match(/round\s*(\d+)/i);
  return match ? Number(match[1]) : fallback;
}

function isCurrentACCSeasonFile(file) {
  return String(file || "").split("/").pop().startsWith("26");
}

function raceRowsFromACC(json) {
  const lines = Array.isArray(json?.sessionResult?.leaderBoardLines)
    ? json.sessionResult.leaderBoardLines
    : [];

  return lines.map((line, index) => {
    const car = line.car || {};
    const driver = (car.drivers && car.drivers[0]) || line.currentDriver || {};
    return {
      id: normalizeId(driver.playerId),
      name: [driver.firstName, driver.lastName].filter(Boolean).join(" ") || driver.shortName || "Driver",
      position: index + 1,
      points: getPointsForPosition(index),
    };
  });
}

function raceRowsFromAC(json) {
  const results = Array.isArray(json?.Result) ? json.Result : [];
  const laps = Array.isArray(json?.Laps) ? json.Laps : [];
  const lapCounts = {};

  laps.forEach((lap) => {
    if (lap.DriverGuid) lapCounts[lap.DriverGuid] = (lapCounts[lap.DriverGuid] || 0) + 1;
  });

  return results
    .map((result) => ({
      id: normalizeId(result.DriverGuid),
      name: result.DriverName || "Driver",
      totalTimeMs: result.TotalTime,
      lapCount: lapCounts[result.DriverGuid] || 0,
    }))
    .sort((a, b) => {
      if (b.lapCount !== a.lapCount) return b.lapCount - a.lapCount;
      const at = Number.isFinite(a.totalTimeMs) ? a.totalTimeMs : Number.MAX_SAFE_INTEGER;
      const bt = Number.isFinite(b.totalTimeMs) ? b.totalTimeMs : Number.MAX_SAFE_INTEGER;
      return at - bt;
    })
    .map((row, index) => ({
      id: row.id,
      name: row.name,
      position: index + 1,
      points: getPointsForPosition(index),
    }));
}

function raceRowsFromAce(json) {
  return aceRaceRows(json);
}

function getDriverName(driver) {
  return driver?.name || "Driver";
}

function getTeamName(driver) {
  return driver?.team || "Private";
}

function makeDriverLookup(drivers) {
  const lookup = new Map();
  (drivers || []).forEach((driver) => {
    lookup.set(normalizeId(driver.id), driver);
  });
  return lookup;
}

export function buildSeries(events, drivers, mode, metric, dropRounds = 0, includeMissedRounds = true) {
  const driverLookup = makeDriverLookup(drivers);
  const droppedEvents = new Map();
  if (dropRounds > 0) {
    const driverIds = new Set(events.flatMap((event) => event.results.map((result) => result.id)));
    driverIds.forEach((id) => {
      const rounds = events.map((event, index) => ({
        index,
        file: event.file || "",
        participated: event.results.some((result) => result.id === id),
        points: event.results.find((result) => result.id === id)?.points || 0,
      })).filter((round) => includeMissedRounds || round.participated);
      rounds.sort((a, b) => a.points - b.points || a.file.localeCompare(b.file));
      droppedEvents.set(id, new Set(rounds.slice(0, dropRounds).map((round) => round.index)));
    });
  }
  const entityMeta = new Map();
  const cumulative = new Map();
  const chartRows = [];

  events.forEach((event, eventIndex) => {
    const rowScores = new Map();

    event.results.forEach((result) => {
      const profile = driverLookup.get(result.id);
      const entityKey = mode === "teams" ? getTeamName(profile) : result.id;
      const entityName = mode === "teams" ? getTeamName(profile) : getDriverName(profile) || result.name;
      if (!entityKey) return;

      entityMeta.set(entityKey, {
        key: entityKey,
        name: entityName,
        team: getTeamName(profile),
        image: profile?.image,
      });
      const points = droppedEvents.get(result.id)?.has(eventIndex) ? 0 : result.points;
      rowScores.set(entityKey, (rowScores.get(entityKey) || 0) + points);
    });

    rowScores.forEach((points, key) => {
      cumulative.set(key, (cumulative.get(key) || 0) + points);
    });

    const chartRow = {
      eventIndex,
      label: `R${event.round}`,
      round: event.round,
      track: event.track,
      date: event.dateLabel,
    };

    entityMeta.forEach((_, key) => {
      chartRow[key] = metric === "cumulative" ? cumulative.get(key) || 0 : rowScores.get(key) || 0;
      chartRow[`${key}__round`] = rowScores.get(key) || 0;
      chartRow[`${key}__total`] = cumulative.get(key) || 0;
    });

    chartRows.push(chartRow);
  });

  const entities = Array.from(entityMeta.values())
    .map((entity) => ({
      ...entity,
      total: cumulative.get(entity.key) || 0,
    }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));

  const finalRows = chartRows.map((row) => {
    entities.forEach((entity) => {
      if (row[entity.key] === undefined) row[entity.key] = metric === "cumulative" ? 0 : 0;
      if (row[`${entity.key}__round`] === undefined) row[`${entity.key}__round`] = 0;
      if (row[`${entity.key}__total`] === undefined) row[`${entity.key}__total`] = 0;
    });
    return row;
  });

  return { chartRows: finalRows, entities };
}

function truncateLabel(value, max = 26) {
  const text = String(value || "");
  return text.length > max ? `${text.slice(0, max - 1)}...` : text;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const visiblePayload = payload
    .filter((item) => item.value !== undefined && item.value !== null)
    .sort((a, b) => Number(b.value) - Number(a.value))
    .slice(0, 8);

  return (
    <Paper
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: "1px solid rgba(255,255,255,0.12)",
        maxWidth: { xs: 260, sm: 360 },
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
        {label} - {row.track}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {row.date}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Stack spacing={0.5}>
        {visiblePayload.map((item) => (
          <Stack key={item.dataKey} direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 8, height: 8, bgcolor: item.color, borderRadius: "50%" }} />
            <Typography variant="caption" sx={{ minWidth: 0, flex: 1 }} noWrap>
              {item.name}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 900 }}>
              {item.value} pts
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

export default function ResultHistory({
  data,
  sim = "ACC",
  initialMode = "drivers",
  lockMode = false,
  title = "Result History",
}) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [mode, setMode] = React.useState(initialMode);
  const [metric, setMetric] = React.useState("cumulative");
  const [applyDropRounds, setApplyDropRounds] = React.useState(false);
  const [limit, setLimit] = React.useState(5);
  const [expanded, setExpanded] = React.useState(false);
  const [selected, setSelected] = React.useState([]);
  const [showPrivateTeam, setShowPrivateTeam] = React.useState(true);
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [errors, setErrors] = React.useState([]);

  const drivers = React.useMemo(() => data?.drivers?.list || [], [data?.drivers?.list]);
  const files = sim === "AC" ? AC_FILES : sim === "AC_EVO" ? AC_EVO_FILES : ACC_FILES;
  const standings = sim === "AC_EVO"
    ? data?.standings?.AC_EVO ?? data?.standings?.ACEVO
    : data?.standings?.[sim];
  const dropRounds = sim === "AC_EVO"
    ? Math.max(0, Number(standings?.dropRounds ?? data?.standings?.dropRounds) || 0)
    : DROP_ROUNDS;

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      const nextEvents = [];
      const nextErrors = [];

      for (const file of files) {
        if (sim === "ACC" && !isCurrentACCSeasonFile(file)) continue;

        const url = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/${file}` : `/${file}`;
        const json = await fetchJson(url);
        if (!mounted) return;

        if (json.__error) {
          nextErrors.push(`${file}: ${json.__error}`);
          continue;
        }

        const isRace = sim === "ACC"
          ? json.sessionType === "R"
          : sim === "AC_EVO"
            ? String(json.session_type || "").toLowerCase() === "race"
            : json.Type === "RACE";
        if (!isRace) continue;

        const date = parseDateFromFilename(file);
        const fallbackRound = nextEvents.length + 1;
        const results = sim === "ACC"
          ? raceRowsFromACC(json)
          : sim === "AC_EVO"
            ? raceRowsFromAce(json)
            : raceRowsFromAC(json);
        nextEvents.push({
          file,
          round: sim === "ACC" ? roundFromACC(json, fallbackRound) : (json.event_index ?? fallbackRound - 1) + 1,
          track: titleCaseTrack(
            sim === "ACC" ? json.trackName || json.metaData : sim === "AC_EVO" ? json.track_name : json.TrackName
          ),
          date,
          dateLabel: date ? date.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "",
          results,
        });
      }

      nextEvents.sort((a, b) => {
        if (a.date && b.date) return a.date - b.date;
        return a.round - b.round;
      });

      if (mounted) {
        setEvents(nextEvents);
        setErrors(nextErrors);
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [files, sim]);

  const { chartRows, entities } = React.useMemo(
    () => buildSeries(events, drivers, mode, metric, applyDropRounds ? dropRounds : 0, sim !== "AC_EVO"),
    [events, drivers, mode, metric, applyDropRounds, dropRounds, sim]
  );

  React.useEffect(() => {
    setMode(initialMode);
    setSelected([]);
  }, [initialMode, sim]);

  React.useEffect(() => {
    setSelected([]);
  }, [mode]);

  const graphEntities = React.useMemo(() => {
    if (mode !== "teams" || showPrivateTeam) return entities;
    return entities.filter((entity) => String(entity.name || "").trim().toLowerCase() !== "private");
  }, [entities, mode, showPrivateTeam]);

  const visibleEntities = React.useMemo(() => {
    if (selected.length > 0) {
      const selectedSet = new Set(selected);
      return graphEntities.filter((entity) => selectedSet.has(entity.key));
    }
    return graphEntities.slice(0, limit);
  }, [graphEntities, limit, selected]);

  const leaders = isSmall ? graphEntities.slice(0, 18) : graphEntities;
  const maxValue = Math.max(1, ...chartRows.flatMap((row) => visibleEntities.map((entity) => row[entity.key] || 0)));

  const toggleEntity = (key) => {
    setSelected((current) => {
      if (current.includes(key)) return current.filter((item) => item !== key);
      return [...current, key].slice(-10);
    });
  };

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        mb: 4,
        borderRadius: { xs: 2, md: 3 },
        border: "1px solid rgba(31,204,220,0.22)",
        background:
          "linear-gradient(180deg, rgba(31,204,220,0.08) 0%, rgba(0,22,59,0.9) 55%, rgba(247,56,59,0.06) 100%)",
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 1.5, md: 2 }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "flex-start" }}
        sx={{ mb: 2 }}
      >
        <Box>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={applyDropRounds}
                onChange={(event) => setApplyDropRounds(event.target.checked)}
                disabled={dropRounds === 0}
              />
            }
            label="Apply drop rounds"
            sx={{ ml: 0, mb: 1 }}
          />
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 900, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
                {title}
              </Typography>
              {expanded && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.82rem", sm: "0.875rem" } }}
                >
                  {sim === "ACC" ? "Current season" : "Championship"} points round by round for{" "}
                  {sim === "ACC"
                    ? "Assetto Corsa Competizione"
                    : sim === "AC_EVO" ? "Assetto Corsa EVO" : "Assetto Corsa"}.
                </Typography>
              )}
            </Box>
            {expanded && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setExpanded((value) => !value)}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  display: { xs: "inline-flex", md: "none" },
                  flex: "0 0 auto",
                  minWidth: 104,
                }}
              >
                Close
              </Button>
            )}
          </Stack>
        </Box>

        {expanded && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            {!lockMode && (
              <ButtonGroup variant="outlined" size="small" fullWidth={isSmall}>
                <Button variant={mode === "drivers" ? "contained" : "outlined"} onClick={() => setMode("drivers")}>
                  Drivers
                </Button>
                <Button variant={mode === "teams" ? "contained" : "outlined"} onClick={() => setMode("teams")}>
                  Teams
                </Button>
              </ButtonGroup>
            )}
            {mode === "teams" && (
              <Box
                component="label"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  minHeight: 32,
                  px: 1,
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <input
                  type="checkbox"
                  checked={showPrivateTeam}
                  onChange={(event) => setShowPrivateTeam(event.target.checked)}
                  style={{
                    accentColor: theme.palette.primary.main,
                    width: 16,
                    height: 16,
                    margin: 0,
                  }}
                />
                Private
              </Box>
            )}
            <ButtonGroup variant="outlined" size="small" fullWidth={isSmall}>
              <Button
                variant={metric === "cumulative" ? "contained" : "outlined"}
                onClick={() => setMetric("cumulative")}
              >
                Total
              </Button>
              <Button variant={metric === "round" ? "contained" : "outlined"} onClick={() => setMetric("round")}>
                Round
              </Button>
            </ButtonGroup>
            <Select
              size="small"
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value))}
              sx={{ minWidth: { xs: "100%", sm: 104 } }}
            >
              <MenuItem value={5}>Top 5</MenuItem>
              <MenuItem value={8}>Top 8</MenuItem>
              <MenuItem value={12}>Top 12</MenuItem>
            </Select>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setExpanded(false)}
              endIcon={<ExpandLessIcon />}
              sx={{ display: { xs: "none", md: "inline-flex" }, minWidth: 132 }}
            >
              Graph only
            </Button>
          </Stack>
        )}
        {!expanded && (
          <Stack direction="row" justifyContent={{ xs: "stretch", md: "flex-end" }} sx={{ width: { xs: "100%", md: "auto" } }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setExpanded(true)}
              endIcon={<ExpandMoreIcon />}
              fullWidth={isSmall}
              sx={{ minWidth: 132 }}
            >
              Full view
            </Button>
          </Stack>
        )}
      </Stack>

      {expanded && errors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {errors.length} result file{errors.length === 1 ? "" : "s"} could not be loaded.
        </Alert>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 7 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            Building championship history...
          </Typography>
        </Stack>
      ) : chartRows.length === 0 ? (
        <Alert severity="info">No race result history is available yet.</Alert>
      ) : (
        <>
          <Box
            sx={{
              width: "100%",
              overflowX: { xs: "auto", md: "hidden" },
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
              pb: { xs: 1, md: 0 },
            }}
          >
            <Box sx={{ height: { xs: 320, sm: 360, md: 430 }, minWidth: { xs: MOBILE_CHART_MIN_WIDTH, md: 0 }, mt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartRows} margin={{ top: 12, right: isSmall ? 16 : 28, bottom: 4, left: 0 }}>
                <defs>
                  <linearGradient id="historyGlow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1fccdc" stopOpacity={0.22} />
                    <stop offset="95%" stopColor="#1fccdc" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke={theme.palette.text.secondary}
                  tick={{ fill: theme.palette.text.secondary, fontSize: isSmall ? 11 : 12 }}
                  tickLine={false}
                  axisLine={{ stroke: "rgba(255,255,255,0.18)" }}
                  interval={isSmall ? 0 : "preserveStartEnd"}
                />
                <YAxis
                  stroke={theme.palette.text.secondary}
                  tick={{ fill: theme.palette.text.secondary, fontSize: isSmall ? 11 : 12 }}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, Math.ceil(maxValue * 1.12)]}
                  width={isSmall ? 32 : 46}
                />
                <Tooltip content={<CustomTooltip />} />
                {!isSmall && <Legend wrapperStyle={{ paddingTop: 8 }} />}
                {visibleEntities[0] && metric === "cumulative" && (
                  <Area
                    type="monotone"
                    dataKey={visibleEntities[0].key}
                    name={visibleEntities[0].name}
                    fill="url(#historyGlow)"
                    stroke="transparent"
                    isAnimationActive
                    activeDot={false}
                    legendType="none"
                  />
                )}
                {visibleEntities.map((entity, index) => (
                  <Line
                    key={entity.key}
                    type="monotone"
                    dataKey={entity.key}
                    name={entity.name}
                    stroke={COLORS[index % COLORS.length]}
                    strokeWidth={isSmall ? (index === 0 ? 3 : 2) : index === 0 ? 4 : 2.5}
                    dot={{ r: isSmall ? 2 : 3, strokeWidth: 1 }}
                    activeDot={{ r: isSmall ? 5 : 7, strokeWidth: 2 }}
                    connectNulls
                    isAnimationActive
                  />
                ))}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          {expanded && (
            <>
              <Divider sx={{ my: { xs: 1.5, md: 2 } }} />
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  overflowX: { xs: "auto", md: "visible" },
                  flexWrap: { xs: "nowrap", md: "wrap" },
                  rowGap: 1,
                  pb: { xs: 0.5, md: 0 },
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {leaders.map((entity, index) => {
                  const active = selected.includes(entity.key);
                  return (
                <Chip
                  key={entity.key}
                  clickable
                  onClick={() => toggleEntity(entity.key)}
                  color={active ? "primary" : "default"}
                  variant={active ? "filled" : "outlined"}
                  sx={{
                    flex: { xs: "0 0 auto", md: "0 1 auto" },
                    borderColor: active ? "primary.main" : "rgba(255,255,255,0.18)",
                    maxWidth: { xs: 220, sm: 280 },
                    minHeight: 34,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                  title={`${index + 1}. ${entity.name} - ${entity.total} pts`}
                  label={`${index + 1}. ${truncateLabel(entity.name, isSmall ? 18 : 28)} - ${entity.total} pts`}
                />
                  );
                })}
                {selected.length > 0 && (
                  <Chip
                    clickable
                    color="secondary"
                    label="Clear picks"
                    onClick={() => setSelected([])}
                    sx={{ flex: { xs: "0 0 auto", md: "0 1 auto" } }}
                  />
                )}
              </Stack>
            </>
          )}
        </>
      )}
    </Paper>
  );
}
